<template>
  <div :class="containerClass">

    <!-- <span style="font-size: 2rem"><b>Start Discussion</b></span> -->
    <h2>Start Discussion</h2>
    <b-form-group
      label="Program"
      label-for="program"
      label-class="form-label"
      v-if="getLoggedInUser.role_type === USER_ROLE_TYPES.SCHOOL"
    >
      <v-select
        id="program"
        class="form-control v-select-custom"
        label="title"
        v-model="program"
        :reduce="pg => pg.id"
        placeholder="Select Program"
        :options="allPrograms"
        :loading="areProgramsLoading"
        @input="fetchTags()"
      >
      </v-select>
    </b-form-group>
    <b-form-group
      label="Tag"
      label-for="tag"
      label-class="form-label"
      v-if="getLoggedInUser.role_type === USER_ROLE_TYPES.SCHOOL"
    >
      <v-select
        id="tag"
        class="form-control v-select-custom"
        label="title"
        v-model="tag"
        :reduce="tg => tg.id"
        placeholder="Select Tag"
        :options="allTags"
        :loading="areTagsLoading"
        :disabled="!program"
      >
      </v-select>
    </b-form-group>
    <b-form-group
      label="Audience"
      label-for="audience"
      label-class="form-label"
      v-if="
        getLoggedInUserDashboard !== DASHBOARD_TYPES.ORGANIZATION &&
          getLoggedInUser.role_type !== USER_ROLE_TYPES.ORGANIZATION
      "
    >
      <div class="custom-select-icon">
        <b-form-select
          class="custom-select-icon__select"
          style="min-width: 185px"
          v-model="audience"
          :options="optionsForAudience"
          :state="!$v.audience.required && $v.audience.$dirty ? false : null"
          :disabled="isAudienceLocked"
        ></b-form-select>
        <span class="material-icons custom-select-icon__icon">sort</span>
      </div>
      <b-form-invalid-feedback :state="!$v.audience.required && $v.audience.$dirty ? false : null"
        >This field is required.</b-form-invalid-feedback
      >
    </b-form-group>
    <b-form-group label="Topic" label-for="topic" label-class="form-label">
      <div class="custom-select-icon">
        <b-form-select
          id="topic"
          class="custom-select-icon__select"
          style="min-width: 185px"
          v-model="topic"
          :options="quesOptions"
          :state="!$v.topic.required && $v.topic.$dirty ? false : null"
        >
          <template #first>
            <b-form-select-option :value="null" hidden>Please select a topic</b-form-select-option>
          </template>
        </b-form-select>
        <span class="material-icons custom-select-icon__icon">sort</span>
      </div>
      <b-form-invalid-feedback :state="!$v.topic.required && $v.topic.$dirty ? false : null"
        >This field is required.</b-form-invalid-feedback
      >
    </b-form-group>

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Title" label-for="quesTitle" label-class="form-label">
        <b-form-input
          id="quesTitle"
          placeholder="Enter question title"
          v-model="quesTitle"
          maxlength="250"
          :state="!$v.quesTitle.required && $v.quesTitle.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Details" label-for="description" label-class="form-label">
        <editor
          :api-key="tinemceApiKey"
          :init="{
            height: 400,
            menubar: 'edit view insert format tools table help',
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount',
            ],
            toolbar:
              'undo redo | formatselect fontselect fontsizeselect | bold italic underline strikethrough forecolor backcolor | \
           alignleft aligncenter alignright alignjustify | \
           bullist numlist outdent indent | link image media table | blockquote code | removeformat | fullscreen preview help',
          }"
          v-model="description"
          placeholder="Enter question details"
        />
        <b-form-invalid-feedback :state="!$v.description.required && $v.description.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-btn variant="primary" :disabled="isLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : 'Post' }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import Editor from '@tinymce/tinymce-vue';
import { get } from 'lodash';
import { required } from 'vuelidate/lib/validators';
import {
  USER_ROLE_TYPES,
  SCL_AUDIENCE_OPT,
  AUDIENCE_OPTIONS,
  PAST_STD_AUDIENCE_OPTION,
  CURRENT_STD_AUDIENCE_OPTION,
  DASHBOARD_TYPES,
} from '@/common/constants';
// import { formatToAPIDate } from '@/common/utils';
// import moment from 'moment';
export default {
  extends: Page,
  components: { Editor },

  data() {
    return {
      title: 'Start Discussion',
      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      quesTitle: '',
      topic: null,
      audience: null,
      description: '',
      isLoading: false,
      isQuestionFetching: false,
      program: null,
      tag: null,
      allPrograms: [],
      allTags: [],
      areProgramsLoading: false,
      areTagsLoading: false,
      quesOptions: [],
      schoolQuesOptions: [
        { value: 'jobs_options', text: 'Jobs/Options' },
        { value: 'exam_testing', text: 'Exam/Testing' },
        { value: 'skills', text: 'Skills' },
        { value: 'class', text: 'Class' },
        { value: 'equipment', text: 'Equipment' },
        { value: 'general', text: 'General' },
        { value: 'miscellaneous', text: 'Miscellaneous' },
      ],
      orgQuesOptions: [
        { value: 'community_qa', text: 'Community Q&A' },
        { value: 'events', text: 'Events' },
        { value: 'general_announcements', text: 'General Announcements' },
        { value: 'job_opportunities', text: 'Job Opportunities' },
        { value: 'resources', text: 'Resources' },
        { value: 'scholarships', text: 'Scholarships' },
        { value: 'success_stories', text: 'Success Stories' },
        { value: 'volunteer_opportunities', text: 'Volunteer Opportunities' },
      ],
      stdStatus: false,
      isStdStatusFetching: false,
      USER_ROLE_TYPES,
      SCL_AUDIENCE_OPT,
      AUDIENCE_OPTIONS,
      PAST_STD_AUDIENCE_OPTION,
      CURRENT_STD_AUDIENCE_OPTION,
      DASHBOARD_TYPES,
    };
  },

  validations() {
    const validations = {
      quesTitle: { required },
      topic: { required },
      description: { required },
    };

    if (
      this.getLoggedInUserDashboard !== DASHBOARD_TYPES.ORGANIZATION &&
      get(this.getLoggedInUser, 'role_type') !== USER_ROLE_TYPES.ORGANIZATION
    ) {
      validations.audience = { required };
    }

    return validations;
  },
  watch: {
    stdStatus: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          this.audience = this.stdStatus ? 'student_from_my_program_and_instructors' : null;
        }
      },
    },
    tag(value) {
      if (value && !this.$route.params.id) {
        this.audience = 'everyone';
      }
    },
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser', 'getLoggedInUserDashboard']),

    isMobSmallScreen() {
      return this.windowWidth <= 420;
    },
    optionsForAudience() {
      if (this.getLoggedInUser.role_type !== USER_ROLE_TYPES.SCHOOL) {
        if (this.stdStatus) {
          return CURRENT_STD_AUDIENCE_OPTION;
        } else {
          return PAST_STD_AUDIENCE_OPTION;
        }
      } else {
        return SCL_AUDIENCE_OPT;
      }
    },
    isAudienceLocked() {
      return Boolean(this.$route.params.id) || (this.tag && this.audience === 'everyone');
    },
  },

  methods: {
    ...mapActions('questions', ['createQuestion', 'getQuestion', 'updateQuestion']),
    ...mapActions('student', ['getStudentStatus']),
    ...mapActions('program', ['getSclPrograms']),
    ...mapActions('attendance', ['getTags']),
    get,
    goBack() {
      this.$router.go(-1);
    },
    async fetchStdStatus() {
      this.isStdStatusFetching = true;
      try {
        const res = await this.getStudentStatus();
        this.stdStatus = res.data.current_student;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isStdStatusFetching = false;
    },
    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getSclPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },
    async fetchTags() {
      if (!this.program) {
        this.allTags = [];
        this.tag = null;
        return;
      }
      this.areTagsLoading = true;
      try {
        const res = await this.getTags({ program__id: this.program });
        this.allTags = res.data.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areTagsLoading = false;
    },
    async fetchQuestion() {
      this.isQuestionFetching = true;
      try {
        const res = await this.getQuestion(this.$route.params.id);
        this.quesTitle = res.data.title;
        this.description = res.data.description;
        this.audience = res.data.audience;
        this.topic = res.data.topic;
        this.program = get(res.data, 'program.id', null);
        this.tag = get(res.data, 'tag.id', null);
        if (this.program) {
          await this.fetchTags();
        }
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isQuestionFetching = false;
    },
    async onSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.isLoading = true;
        try {
          const data = {
            title: this.quesTitle,
            topic: this.topic,
            description: this.description,
          };

          // Handle organization discussions
          if (
            this.getLoggedInUserDashboard === DASHBOARD_TYPES.ORGANIZATION ||
            get(this.getLoggedInUser, 'role_type') === USER_ROLE_TYPES.ORGANIZATION
          ) {
            data.audience = 'everyone';
            data.organization = get(this.getLoggedInUser, 'linked_entity.id');
          } else {
            data.audience = this.audience;
          }

          if (this.getLoggedInUser.role_type === this.USER_ROLE_TYPES.SCHOOL) {
            if (this.$route.params.id) {
              data.program = this.program || null;
              data.tag = this.tag || null;
            } else {
              if (this.program) data.program = this.program;
              if (this.tag) data.tag = this.tag;
            }
          }

          let response;
          if (this.$route.params.id) {
            response = await this.updateQuestion({ id: this.$route.params.id, data });
            this.makeToast({ variant: 'success', msg: 'Discussion updated!' });
          } else {
            response = await this.createQuestion(data);
            this.makeToast({ variant: 'success', msg: 'Discussion started!' });
          }

          if (get(response, 'data.data.id')) {
            setTimeout(
              () => this.$router.push({ name: 'student-question-details', params: { id: response.data.data.id } }),
              250,
            );
          } else {
            setTimeout(() => this.$router.push({ name: 'my-questions' }), 250);
          }
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
        this.isLoading = false;
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields.' });
      }
    },
  },
  async mounted() {
    // Set topic options based on dashboard type
    if (
      this.getLoggedInUserDashboard === DASHBOARD_TYPES.ORGANIZATION ||
      get(this.getLoggedInUser, 'role_type') === USER_ROLE_TYPES.ORGANIZATION
    ) {
      this.quesOptions = this.orgQuesOptions;
    } else {
      this.quesOptions = this.schoolQuesOptions;
    }

    if (this.getLoggedInUser.role_type === USER_ROLE_TYPES.STUDENT) {
      this.fetchStdStatus();
    }
    if (this.getLoggedInUser.role_type === USER_ROLE_TYPES.SCHOOL) {
      await this.fetchPrograms();
    }
    if (this.$route.params.id) {
      this.fetchQuestion();
    }
  },
};
</script>

<style></style>
